<template>
	<app-button v-if="canEdit" icon="plus" theme="primary" size="small" @click="open = true">
		Add YouTube Video
	</app-button>

	<slideover title="Add YouTube Video" :open="open" @close="close">
		<form @submit.prevent="submitForm">
			<div class="mb-4">
				<label for="video-url" class="block text-sm font-medium text-gray-700"
					>YouTube URL</label
				>
				<input
					id="video-url"
					v-model="form.url"
					type="url"
					required
					class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
					:class="{ 'border-red-500': error }" />
				<p v-if="error" class="mt-1 text-sm text-red-600">{{ error }}</p>
			</div>
			<div class="mb-4">
				<label for="video-description" class="block text-sm font-medium text-gray-700"
					>Description</label
				>
				<input
					id="video-description"
					v-model="form.description"
					type="text"
					required
					class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50" />
			</div>
		</form>
		<template #action>
			<div class="w-full">
				<div class="flex justify-end">
					<app-button :loading="loading" @click="submitForm">Save</app-button>
				</div>
			</div>
		</template>
	</slideover>
</template>

<script lang="ts" setup>
	import { ref, reactive } from 'vue'
	import AppButton from '@/components/base/AppButton.vue'
	import ProductService from '@/services/product'
	import useToaster from '@/stores/toaster'

	const props = defineProps<{
		productId: string | number
		canEdit: boolean
	}>()

	const emit = defineEmits(['video-added'])

	const toaster = useToaster()

	const open = ref(false)
	const loading = ref(false)
	const error = ref('')
	const form = reactive({
		url: '',
		description: ''
	})

	const validateYouTubeUrl = (url: string) => {
		const youtubePattern =
			/^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
		return youtubePattern.test(url)
	}

	const submitForm = async () => {
		if (!props.productId) return

		error.value = ''
		if (!validateYouTubeUrl(form.url)) {
			error.value =
				'Please enter a valid YouTube URL (e.g., https://www.youtube.com/watch?v=VIDEO_ID)'
			return
		}

		loading.value = true
		try {
			const api = new ProductService()
			await api.addVideo(props.productId, form)
			toaster.success({ text: 'Video link added successfully!' })
			emit('video-added')
			close()
		} catch (error) {
			console.error('Error adding video link:', error)
			toaster.error({ text: 'Failed to add video link.' })
		} finally {
			loading.value = false
		}
	}

	const close = () => {
		open.value = false
		form.url = ''
		form.description = ''
		error.value = ''
	}
</script>
